// HypeMeter — live progress bar on a stream, polls /api/hype/active/{streamId}
import { useEffect, useState } from "react";
import { Flame, X, Plus } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";

export default function HypeMeter({ streamId, isOwnerOrCreator, currentUserRole }) {
  const [meter, setMeter] = useState(null);
  const [open, setOpen] = useState(false);
  const [goal, setGoal] = useState(1000);
  const [title, setTitle] = useState("HYPE GOAL");

  const load = () => api.get(`/hype/active/${streamId}`).then(({ data }) => setMeter(data)).catch(() => {});
  useEffect(() => {
    if (!streamId) return;
    load();
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [streamId]);

  const start = async () => {
    try {
      const { data } = await api.post("/hype/start", { stream_id: streamId, goal: parseInt(goal), title });
      setMeter(data); setOpen(false); toast.success("Hype meter is LIVE");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to start"); }
  };

  const close = async () => {
    if (!meter) return;
    try { await api.post(`/hype/${meter.id}/close`); setMeter(null); toast.info("Hype meter closed"); }
    catch (e) { toast.error("Close failed"); }
  };

  const canControl = isOwnerOrCreator || currentUserRole === "owner";

  if (!meter && !canControl) return null;

  if (!meter && canControl) {
    return (
      <div className="absolute top-20 left-4 z-30">
        <button onClick={() => setOpen(true)} className="bg-nexus-amber/30 hover:bg-nexus-amber/50 text-nexus-amber border border-nexus-amber/50 px-3 py-1.5 text-[10px] tracking-[0.3em] uppercase font-display flex items-center gap-1" data-testid="hype-start-btn">
          <Flame className="w-3 h-3" /> Start Hype
        </button>
        {open && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-6" onClick={() => setOpen(false)}>
            <div className="glass-strong p-5 max-w-sm w-full space-y-3" onClick={(e) => e.stopPropagation()} data-testid="hype-modal">
              <div className="flex items-center justify-between">
                <h3 className="font-display text-lg flex items-center gap-2"><Flame className="w-4 h-4 text-nexus-amber" />Start Hype Meter</h3>
                <button onClick={() => setOpen(false)}><X className="w-4 h-4 text-white/40" /></button>
              </div>
              <input value={title} onChange={(e) => setTitle(e.target.value)} className="w-full bg-transparent border border-white/10 px-3 py-2 text-sm" data-testid="hype-title" placeholder="Goal title (e.g. RAID PARTY)" />
              <div>
                <div className="label-eyebrow mb-1.5">goal · NX</div>
                <input type="number" value={goal} onChange={(e) => setGoal(e.target.value)} className="w-full bg-transparent border border-white/10 px-3 py-2 text-sm" data-testid="hype-goal-input" />
                <div className="flex gap-1 mt-1.5">
                  {[500, 1000, 2500, 5000, 10000].map((g) => (
                    <button key={g} onClick={() => setGoal(g)} className="text-[10px] tracking-wider px-2 py-1 border border-white/10 hover:border-nexus-amber">{g}</button>
                  ))}
                </div>
              </div>
              <button onClick={start} className="btn-nexus w-full justify-center" data-testid="hype-fire"><Flame className="w-3.5 h-3.5" /> Ignite hype</button>
            </div>
          </div>
        )}
      </div>
    );
  }

  if (!meter) return null;
  const pct = Math.min(100, Math.round((meter.current_nx / meter.goal_nx) * 100));
  const achieved = !!meter.achieved_at;

  return (
    <div className="absolute top-20 left-4 right-4 lg:right-auto lg:max-w-md z-30 pointer-events-none" data-testid="hype-meter">
      <div className="bg-black/70 backdrop-blur-md border border-nexus-amber/50 p-3 space-y-2 pointer-events-auto">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Flame className={`w-4 h-4 ${achieved ? "text-nexus-mint" : "text-nexus-amber animate-pulse"}`} />
            <div className="text-[10px] tracking-[0.32em] uppercase font-display" style={{ color: achieved ? "#00FF66" : "#FFC107" }}>
              {achieved ? "★ GOAL HIT" : meter.title}
            </div>
          </div>
          {canControl && (
            <button onClick={close} className="text-[9px] tracking-wider text-white/40 hover:text-white" data-testid="hype-close">close</button>
          )}
        </div>
        <div className="font-display font-black text-xl">
          {meter.current_nx.toLocaleString()} <span className="text-white/30 text-sm">/ {meter.goal_nx.toLocaleString()} NX</span>
        </div>
        <div className="h-2.5 bg-white/10 overflow-hidden">
          <div className={`h-full hype-bar-active`} style={{ width: `${pct}%`, background: achieved ? "#00FF66" : "linear-gradient(90deg, #FFC107, #FF3366)", color: achieved ? "#00FF66" : "#FFC107", boxShadow: `0 0 16px ${achieved ? "#00FF66" : "#FFC107"}` }} />
        </div>
        <div className="flex justify-between text-[9px] tracking-[0.3em] uppercase text-white/50">
          <span>{pct}%</span>
          <span>by {meter.started_by_name}</span>
        </div>
      </div>
    </div>
  );
}
